
import { SurveyStore } from "types/store"
import { UploadDocStore } from "types/store"
import { PATH } from "./models"
import { DoctorType, Gender } from "types/entity"

export type UiView =
  | "carousel"
  | "checkbox"
  | "selector"
  | "inline"
  | "icon"
  | "fat-button"
  | "button"
  | "input"
  | "code"
  | "radiogroup"
  | "questions_line"
  | "slider"
export type UiPosition = "left" | "right" | "middle"
export type UiStyle = "primary" | "default" | "accent" | "warning" | "info"
export type Value =
  | string
  | number
  | boolean
  | undefined
  | unknown
  | { identifier: string; [key: string]: any }

export function isValueWithIdentifier(
  v: Value,
): v is { identifier: string; [key: string]: any } {
  return (
    v !== null &&
    typeof v === "object" &&
    !Array.isArray(v) &&
    "identifier" in v
  )
}

/** Primitives : === ; valeurs structurées : comparaison sur `identifier`. */
export function valueEquals(a: Value, b: Value): boolean {
  if (isValueWithIdentifier(a) && isValueWithIdentifier(b)) {
    return a.identifier === b.identifier
  }
  return a === b
}

export enum ACTION {
  default = "default",
  subquestion = "subquestion",
  redirect = "redirect",
  nothing = "nothing",
  inability = "inability",
}
export enum QUESTION_TYPE {
  redirect = "redirect",
  default = "question",
  loader = "loader",
}

export interface UiSurvey {
  view?: UiView
  position?: UiPosition
  preventAddMore?: boolean
  itemByRow?: number
}

export interface QuestionLanguage {
  id: string
  patient?: { title?: string; subtitle?: string; defaultReply?: string }
  relative?: { title?: string; subtitle?: string; defaultReply?: string }
}
export interface AlertMessage {
  type: "info" | "warning" | "danger" | "success"
  description?: string | JSX.Element
  style?: React.CSSProperties
  img?: string
}
export interface InfoItem {
  imgUrl?: string
  text: string
}
export interface Question {
  title: string
  label?: string
  titleCustomStyle?: React.CSSProperties
  subtitle?: string
  subtitleCustomStyle?: React.CSSProperties
  sectionId?: number
  sourceId?: string | number
  pdf?: { type?: string }
  id: string
  subquestion?: Question
  multipleReplies?: boolean
  maxReplies?: number
  skipable?: boolean
  replies: Reply[]
  defaultReply?: Reply
  informationText?: string
  // Read-only pictogram + HTML text lines displayed under the title
  infoItems?: InfoItem[]
  ui?: UiSurvey
  imgUrl?: string
  order?: string[] // order of the elements to display
  doNotAnimateTitle?: boolean
  // if type === redirect,
  // the object need a value
  // it means the step is not a question but an action, redirect to an other page.
  // (Nir page for instance)
  alert?: AlertMessage
  type?: QUESTION_TYPE
  value?: string
  allowSkip?: boolean
  // Bilan Prévention `personalization` metadata block.
  qNum?: number
  themes?: number[]
  genderOnly?: Gender
  conditionalOn?: { questionId: string; showIfValues: Value[] }
  themeSelector?: boolean
  mandatory?: boolean
  autofillGender?: boolean
  preventionAlertRule?: "bmi"
}
interface Ui {
  style?: UiStyle
  type?: "link" | "text" | "ghost" | "default" | "primary" | "dashed"
  view?: UiView
  buttonStyle?: React.CSSProperties
}
export interface ReplyAction {
  value: Value
  title: string
  subtitle?: string
  action?: ACTION
  imgUrl?: string
  icon?: JSX.Element
  ui?: Ui
  preselect?: boolean
  min?: number,
  max?: number,
  unit?: string
  gender?: Gender
  preventionAlert?: boolean
}
export interface Reply extends ReplyAction {
  additionalActions?: ReplyAction[]
  preventionReportAllowed?: boolean
}

export type Status = "processing" | "answered" | "to_ask"

export interface Answer {
  value: Value[]
  questionId: string
  questionTitle?: string,
  label?: string,
}

export type DocumentNavigation = "forward" | "back"

export interface SurveyProps extends SurveyStore {
  pathway?: DoctorType
  answer: Value[]
  redirect: boolean
  loading: boolean
  previous: () => void
  next: () => void
  set: (r: Reply[]) => void
  unset: (r?: Reply[]) => void
  unsetStepReplies: (payload: { replies?: Reply[]; id?: PATH }) => void
  cancel: () => void
  reset: () => void
  resetReplies: () => void
  submit: (a: Answer[]) => void
  history: { push: (a: string) => void }
  getUploadedDocuments: (navigation?: DocumentNavigation) => void
  setStepReplies: (payload) => void
}

export interface Step {
  title: string
  titleCustomStyle?: React.CSSProperties
  subtitle?: string
  id: string
  multipleReplies?: boolean
  replies: Reply[]
  defaultReply?: Reply
  servicesToDisplay?: string
  ui?: UiSurvey
  imgUrl?: string[] | string
  type?: QUESTION_TYPE
  value?: string
}

export interface DocType {
  type: string
  imgUrl: string
  validated: boolean
}

export interface UploadedDocumentType {
  medicalType: Value
  name: string
  relativeId?: string
  file: string[]
}

export interface UploadDocProps extends UploadDocStore {
  answer: Value[]
  redirect: boolean
  loading: boolean
  previous: () => void
  addImage: (imgSrc: DocType[]) => void
  submitDocument: (doc: UploadedDocumentType) => void
  sendMeLink: (link) => void
  generateQrCode: (path?: string, standalone?: boolean) => void
  fetchNewDocuments: (linkCreatedAt: string | Date, standalone?: boolean) => void
  fetchDermatoPatientDetails: () => void
  next: () => void
  set: (r: Reply[]) => void
  unset: (r?: Reply[]) => void
  cancel: () => void
  reset: () => void
  resetStatus: () => void
  submit: (a: Answer[]) => void
  history: { push: (a: string) => void }
  documents: UploadedDocumentType[]
}

export interface Survey {
  color: string;
  content: Section[];
}

export interface Section {
  id: number;
  label: string;
  questions: TemplateQuestion[];
}

export type TemplateQuestion =
  | RadioGroupQuestion
  | CheckboxQuestion
  | DropdownQuestion
  | TextFieldQuestion;

export interface QuestionPersonalization {
  themes?: number[];
  genderOnly?: "female" | "male";
  themeSelector?: boolean;
  mandatory?: boolean;
  autofillGender?: boolean;
  // Subquestion injected into the previous question.
  bis?: boolean;
  conditionalOn?: { parentId: number; showIfAnswers: number[] };
}

interface BaseQuestion {
  id: number;
  title: string;
  label?: string;
  body?: string;
  type: QuestionType;
  settings: QuestionSettings;
  personalization?: QuestionPersonalization;
  // "bmi": alert when weight / height² leaves 18.5–25 (weight question, height in the same section).
  alert?: "bmi";
}

export type QuestionType =
  | "radiogroup"
  | "checkbox"
  | "dropdown"
  | "textfield";

export interface RadioGroupQuestion extends BaseQuestion {
  type: "radiogroup";
  settings: OptionsSettings;
}

export interface CheckboxQuestion extends BaseQuestion {
  type: "checkbox";
  settings: OptionsSettings;
}

export interface DropdownQuestion extends BaseQuestion {
  type: "dropdown";
  settings: DropdownSettings;
}

export interface TextFieldQuestion extends BaseQuestion {
  type: "textfield";
  settings: TextFieldSettings;
}

export interface OptionsSettings {
  options: Option[];
}
export interface Option {
  id: number;
  label: string;
  gender?: "female" | "male";
  alert?: boolean;
}
export interface DropdownSettings {
  unit: string;
  min: number;
  max: number;
  step: number;
  round: number;
  initial: string;
}
export interface TextFieldSettings {
  placeholder: string;
}

export type QuestionSettings =
  | OptionsSettings
  | DropdownSettings
  | TextFieldSettings;
